import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { IconSparkles } from '@tabler/icons-react';
import { AnchoredPopover } from '../automation/AnchoredPopover';
import { safeHttpUrl } from '../../../lib/security/safe-http-url';
import type { ReportAiContext } from '../../../functions/api/gemini/content-pipeline/types';

/** Literal display span for an entity already selected by transcript analysis. */
interface InsightSegment {
  text: string;
  start: number;
  insight?: ReportAiContext;
}

/** Locate literal mentions for display only; this never selects entities or initiates research. */
function segmentsForText(text: string, insights: ReportAiContext[]): InsightSegment[] {
  const matches: { start: number; end: number; insight: ReportAiContext }[] = [];
  for (const insight of insights) {
    for (const needle of new Set([insight.mentionText, insight.entity].filter((value): value is string => Boolean(value)))) {
      let start = text.indexOf(needle);
      while (start !== -1) {
        const end = start + needle.length;
        const before = text[start - 1] ?? '';
        const after = text[end] ?? '';
        const hasWordBoundary = !/[\p{L}\p{N}_]/u.test(before) && !/[\p{L}\p{N}_]/u.test(after);
        const domainContinues = needle.includes('.') && (before === '.' || (after === '.' && /[\p{L}\p{N}]/u.test(text[end + 1] ?? '')));
        if (hasWordBoundary && !domainContinues) matches.push({ start, end, insight });
        start = text.indexOf(needle, end);
      }
    }
  }
  matches.sort((a, b) => a.start - b.start || b.end - a.end);
  const segments: InsightSegment[] = [];
  let cursor = 0;
  for (const match of matches) {
    if (match.start < cursor) continue;
    if (match.start > cursor) segments.push({ start: cursor, text: text.slice(cursor, match.start) });
    segments.push({ start: match.start, text: text.slice(match.start, match.end), insight: match.insight });
    cursor = match.end;
  }
  if (cursor < text.length) segments.push({ start: cursor, text: text.slice(cursor) });
  return segments;
}

/** Dotted annotation with a hoverable, tappable, keyboard-accessible source panel. */
function InsightTrigger({ insight, label, children }: { insight: ReportAiContext; label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const restoringFocus = useRef(false);
  const pendingFocusReturn = useRef(false);
  const id = useId();

  /** Keep the panel open while crossing the small gap to its links. */
  const show = () => { clearTimeout(hideTimer.current); setOpen(true); };
  /** Mouse departure closes only transient hover previews. */
  const leave = () => {
    clearTimeout(hideTimer.current);
    if (!pinned) hideTimer.current = setTimeout(() => setOpen(false), 180);
  };
  useEffect(() => {
    if (open || !pendingFocusReturn.current) return;
    pendingFocusReturn.current = false;
    // Wait for portal removal before restoring focus; removal can blur a focused source.
    restoringFocus.current = true;
    anchorRef.current?.focus();
    restoringFocus.current = false;
  }, [open]);
  useEffect(() => {
    if (!open || !pinned) return undefined;
    // Explicit activation moves keyboard focus into the portalled source panel.
    const timer = setTimeout(() => panelRef.current?.querySelector<HTMLAnchorElement>('a')?.focus(), 0);
    return () => clearTimeout(timer);
  }, [open, pinned]);
  useEffect(() => () => clearTimeout(hideTimer.current), []);
  useEffect(() => {
    if (!open) return undefined;
    /** Dismiss when focus or the pointer moves outside both trigger and portalled panel. */
    const dismissOutside = (event: Event) => {
      if (event.target instanceof Node && !anchorRef.current?.contains(event.target) && !panelRef.current?.contains(event.target)) {
        setOpen(false); setPinned(false);
      }
    };
    /** Restore focus after explicit keyboard dismissal without reopening the preview. */
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault(); event.stopPropagation();
      clearTimeout(hideTimer.current);
      pendingFocusReturn.current = true;
      setOpen(false); setPinned(false);
    };
    document.addEventListener('pointerdown', dismissOutside);
    document.addEventListener('focusin', dismissOutside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', dismissOutside);
      document.removeEventListener('focusin', dismissOutside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  return <span onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
    <button ref={anchorRef} type="button" aria-label={label} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined}
      className="inline cursor-pointer bg-transparent p-0 text-inherit underline decoration-dotted decoration-muted-foreground underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-volt rounded-sm"
      onMouseEnter={show} onMouseLeave={leave}
      onFocus={() => { if (!restoringFocus.current) show(); }}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          event.preventDefault(); clearTimeout(hideTimer.current); setOpen(false); setPinned(false);
        }
      }}
      onClick={() => { clearTimeout(hideTimer.current); setPinned(!pinned); setOpen(!pinned); }}>
      {children}<IconSparkles aria-hidden="true" className="inline-block w-3 h-3 ml-0.5 align-baseline text-muted-foreground" />
    </button>
    {open && <AnchoredPopover anchorRef={anchorRef} panelRef={panelRef} className="z-[9999] w-80 overflow-y-auto rounded-xl border border-border bg-surface shadow-xl">
      <div id={id} className="p-4" role="dialog" aria-label="AI context" onMouseEnter={show} onMouseLeave={leave}
        onKeyDown={event => {
          if (event.key === 'Escape') {
            event.preventDefault(); pendingFocusReturn.current = true; setOpen(false); setPinned(false);
          }
        }}>
        <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><IconSparkles className="w-3.5 h-3.5" aria-hidden="true" />AI context</p>
        <p className="mt-2 text-sm leading-relaxed text-foreground">{insight.summary}</p>
        <div className="mt-3 flex flex-wrap gap-3">
          {insight.sources.filter(source => safeHttpUrl(source.url)).map(source => <a key={source.url} href={source.url} target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground underline hover:text-foreground">{source.title}</a>)}
        </div>
        <time dateTime={insight.retrievedAt} className="mt-2 block text-xs text-subtle-foreground">{insight.retrievedAt.slice(0, 10)}</time>
      </div>
    </AnchoredPopover>}
  </span>;
}

/** Annotate exact mentions while preserving original text and optional timed-word rendering. */
export function InsightText({ text, insights = [], renderText }: {
  text: string;
  insights?: ReportAiContext[];
  renderText?: (text: string, start: number, annotated: boolean) => ReactNode;
}) {
  return <>{segmentsForText(text, insights).map(segment => {
    const content = renderText ? renderText(segment.text, segment.start, Boolean(segment.insight)) : segment.text;
    return segment.insight
      ? <InsightTrigger key={segment.start} insight={segment.insight} label={segment.text}>{content}</InsightTrigger>
      : <span key={segment.start}>{content}</span>;
  })}</>;
}
